import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Modal,
  Form,
  Input,
  Table,
  Tag,
  Checkbox,
  Dropdown,
  List,
  Empty,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  LockOutlined,
  UserOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  CalendarOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FolderOutlined,
  ToolOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import lang from "../../helper/langHelper";

// Per-category icon, display label, and sort order for the Permissions
// picker — keyed off the same category strings the backend registry uses
// (backend-Imergey/src/Middlewares/Permission/registry.ts route metadata).
// The catalog itself arrives in whatever order requirePermission() calls
// happened to register at server startup (effectively arbitrary — router
// require() order), so both the category order and each category's own
// icon/label live here as the single deliberate presentation layer on
// top of that.
const CATEGORY_META = {
  calendar: { icon: CalendarOutlined, label: "Calendar" },
  content: { icon: FileTextOutlined, label: "Content" },
  approval: { icon: CheckCircleOutlined, label: "Approval" },
  media: { icon: PictureOutlined, label: "Media" },
  organization: { icon: FolderOutlined, label: "Organization" },
  tools: { icon: ToolOutlined, label: "Tools" },
  analytics: { icon: BarChartOutlined, label: "Analytics" },
  account: { icon: UserOutlined, label: "Account" },
};
const CATEGORY_ORDER = Object.keys(CATEGORY_META);

// Stable, deliberate render order for the picker: known categories first
// (in CATEGORY_ORDER), any future/unrecognized category appended
// alphabetically after rather than dropped — and permissions within each
// category sorted by label so the list doesn't reshuffle between renders
// depending on server registration order.
const sortCatalogForDisplay = (catalog) => {
  const known = CATEGORY_ORDER.filter((c) => catalog[c]);
  const unknown = Object.keys(catalog)
    .filter((c) => !CATEGORY_ORDER.includes(c))
    .sort();
  return [...known, ...unknown].map((category) => [
    category,
    [...catalog[category]].sort((a, b) => a.label.localeCompare(b.label)),
  ]);
};

// The "Content" category mixes Post and Blog permissions together — sorted
// alphabetically they interleave (Create Blog, Create Post, Delete Post,
// Edit Blog, ...), which reads as a jumble rather than two related sets.
// Splits them into their own column each instead. Falls back to a single
// "Other" column for any content permission that's neither (there
// currently are none, but this keeps a future addition from silently
// disappearing rather than crashing).
const splitPostsAndBlogs = (perms) => {
  const posts = perms.filter((p) => /post/i.test(p.key) && !/blog/i.test(p.key));
  const blogs = perms.filter((p) => /blog/i.test(p.key));
  const other = perms.filter((p) => !posts.includes(p) && !blogs.includes(p));
  return { posts, blogs, other };
};

// Admin-side "Assistants" tab on a single Client's profile — lets Admin
// create/manage Assistants on behalf of that Client (spec requirement 1).
// Straight port of user-panel-Imergey/src/pages/assistants/Index.js,
// retargeted from /user/assistants to /admin/customer/:id/assistants
// (server-side gated by Authentication.admin + checkPermission("customerManager", ...)
// — the Assistants tab itself is only rendered when customerManagerPermission.view
// is true, see viewclients.js).
// clientIdProp lets this be embedded outside the /view-clients/:id route too
// (the "Manage Assistants" quick action on the Customer Management list —
// see User.js — opens this in a Modal with the row's _id, no route param).
const AssistantsTab = ({ clientId: clientIdProp } = {}) => {
  const { id: clientIdFromRoute } = useParams();
  const clientId = clientIdProp || clientIdFromRoute;
  const { request } = useRequest();
  const [assistants, setAssistants] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [resetPasswordFor, setResetPasswordFor] = useState(null);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();

  const base = `/admin/customer/${clientId}/assistants`;

  const fetchAssistants = () => {
    setLoading(true);
    request({
      url: base,
      method: "GET",
      // ResponseHelper.ok() auto-wraps an array payload as { data: [...] }
      // (backend-Imergey/src/helpers/ResponseHelper.ts), so the list
      // controller's array response arrives here as res.data.data.
      //
      // admin-Imergey's useRequest has no onFinally — reset loading in both
      // outcomes explicitly, or the Table spins forever on error.
      onSuccess: (res) => {
        setAssistants(res.data?.data || []);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const fetchCatalog = () => {
    request({
      url: `${base}/permissions/catalog`,
      method: "GET",
      onSuccess: (res) => setCatalog(res.data?.grouped || {}),
    });
  };

  useEffect(() => {
    fetchAssistants();
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      designation: record.assistantDesignation,
      assistantRoleLabel: record.assistantRoleLabel,
      permissions: record.assistantPermissions || [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    if (editing) {
      await request({
        url: `${base}/${editing._id}`,
        method: "PUT",
        data: {
          name: values.name,
          designation: values.designation,
          assistantRoleLabel: values.assistantRoleLabel,
        },
        onSuccess: () => ShowToast("Assistant updated", Severty.SUCCESS),
        onError: (err) => ShowToast(err?.response?.data?.message || "Failed to update", Severty.ERROR),
      });
      await request({
        url: `${base}/${editing._id}/permissions`,
        method: "PUT",
        data: { permissions: values.permissions || [] },
        onSuccess: () => {
          ShowToast("Permissions updated", Severty.SUCCESS);
          setModalOpen(false);
          fetchAssistants();
        },
        onError: (err) => ShowToast(err?.response?.data?.message || "Failed to update permissions", Severty.ERROR),
      });
    } else {
      submitCreateAssistant(values, false);
    }
  };

  // assignExisting: true only on the confirmed retry after the backend
  // reports the email already belongs to an Assistant elsewhere
  // (EXISTING_ASSISTANT below) — never inferred, so this can't attach
  // someone else's existing Assistant account just by mistyping an email.
  const submitCreateAssistant = (values, assignExisting) => {
    request({
      url: base,
      method: "POST",
      data: {
        name: values.name,
        email: values.email,
        designation: values.designation,
        assistantRoleLabel: values.assistantRoleLabel,
        permissions: values.permissions || [],
        assignExisting,
      },
      onSuccess: () => {
        ShowToast(assignExisting ? "Existing Assistant assigned to this Client" : "Assistant invited", Severty.SUCCESS);
        setModalOpen(false);
        fetchAssistants();
      },
      onError: (err) => {
        const body = err?.response?.data;
        if (body?.statusText === "EXISTING_ASSISTANT") {
          Modal.confirm({
            title: lang("Assign existing Assistant?"),
            content: body.message,
            okText: lang("Assign"),
            onOk: () => submitCreateAssistant(values, true),
          });
          return;
        }
        ShowToast(body?.message || "Failed to create assistant", Severty.ERROR);
      },
    });
  };

  const toggleStatus = (record) => {
    const nextStatus = record.assistantStatus === "active" ? "disabled" : "active";
    request({
      url: `${base}/${record._id}/status`,
      method: "PUT",
      data: { status: nextStatus },
      onSuccess: () => {
        ShowToast(
          nextStatus === "disabled"
            ? "Assistant disabled — signed out of all sessions"
            : `Assistant ${nextStatus}`,
          Severty.SUCCESS
        );
        fetchAssistants();
      },
      onError: (err) => ShowToast(err?.response?.data?.message || "Failed to update status", Severty.ERROR),
    });
  };

  const openResetPassword = (record) => {
    resetPasswordForm.resetFields();
    setResetPasswordFor(record);
  };

  const submitResetPassword = (values) => {
    request({
      url: `${base}/${resetPasswordFor._id}/reset-password`,
      method: "PUT",
      data: { newPassword: values.newPassword },
      onSuccess: () => {
        ShowToast("Password reset — the assistant has been signed out of all sessions", Severty.SUCCESS);
        setResetPasswordFor(null);
      },
      onError: (err) => ShowToast(err?.response?.data?.message || "Failed to reset password", Severty.ERROR),
    });
  };

  const removeAssistant = (record) => {
    request({
      url: `${base}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        ShowToast("Assistant deleted", Severty.SUCCESS);
        fetchAssistants();
      },
      onError: (err) => ShowToast(err?.response?.data?.message || "Failed to delete", Severty.ERROR),
    });
  };

  const openAudit = (record) => {
    setAuditFor(record);
    setAuditLoading(true);
    request({
      url: `${base}/${record._id}/audit-log`,
      method: "GET",
      onSuccess: (res) => setAuditLogs(res.data?.logs || []),
      onError: () => setAuditLoading(false),
    }).finally(() => setAuditLoading(false));
  };

  const formatWhen = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return sameDay ? `Today, ${time}` : `${d.toLocaleDateString()}, ${time}`;
  };

  const columns = [
    // Explicit widths on every column — previously only Actions had one, so
    // the table almost always fell into horizontal-scroll mode on normal
    // screen widths, and that scrollbar rendered right across the (short)
    // Actions row, visually slicing through the icon buttons. Sizing every
    // column removes the scrollbar in the common case.
    { title: lang("Name"), dataIndex: "name", width: 120 },
    { title: lang("Email"), dataIndex: "email", width: 170 },
    { title: lang("Role"), dataIndex: "assistantRoleLabel", width: 110 },
    {
      title: lang("Status"),
      dataIndex: "assistantStatus",
      width: 90,
      render: (status) => (
        <Tag color={status === "active" ? "green" : status === "invited" ? "blue" : "red"}>{status}</Tag>
      ),
    },
    {
      title: lang("Permissions"),
      dataIndex: "assistantPermissions",
      width: 100,
      render: (perms) => <span>{(perms || []).length} granted</span>,
    },
    { title: lang("Last Login"), dataIndex: "lastLogin", width: 130, render: (v) => (v ? new Date(v).toLocaleString() : "—") },
    { title: lang("Created By"), dataIndex: "createdBy", width: 110, render: (v) => v?.name || "—" },
    {
      title: lang("Actions"),
      width: 90,
      // Pinned to the right — otherwise the table's own horizontal scroll
      // runs its scrollbar straight through this column. A single dropdown
      // trigger replaces the previous row of 5 icon buttons, which cramped
      // this column and looked cluttered at normal table width.
      fixed: "right",
      render: (_, record) => {
        const isActive = record.assistantStatus === "active";
        const menuProps = {
          onClick: ({ key }) => {
            if (key === "edit") openEdit(record);
            else if (key === "audit") openAudit(record);
            else if (key === "toggle-status") {
              if (isActive) {
                Modal.confirm({
                  title: lang("Disable this assistant?"),
                  icon: <ExclamationCircleOutlined />,
                  content: lang("They will be signed out of all existing sessions immediately."),
                  okText: lang("Disable"),
                  okButtonProps: { danger: true },
                  onOk: () => toggleStatus(record),
                });
              } else {
                toggleStatus(record);
              }
            } else if (key === "reset-password") openResetPassword(record);
            else if (key === "delete") {
              Modal.confirm({
                title: lang("Delete this assistant?"),
                icon: <ExclamationCircleOutlined />,
                okText: lang("Delete"),
                okButtonProps: { danger: true },
                onOk: () => removeAssistant(record),
              });
            }
          },
          items: [
            { key: "edit", icon: <EditOutlined />, label: lang("Edit") },
            { key: "audit", icon: <HistoryOutlined />, label: lang("Audit Log") },
            {
              key: "toggle-status",
              icon: isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
              label: isActive ? lang("Disable") : lang("Enable"),
            },
            { key: "reset-password", icon: <LockOutlined />, label: lang("Reset Password") },
            { type: "divider" },
            { key: "delete", icon: <DeleteOutlined />, label: lang("Delete"), danger: true },
          ],
        };
        return (
          <Dropdown menu={menuProps} trigger={["click"]}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="tab-content">
      <Card
        // Custom flex-wrap header instead of Card's title/extra props —
        // those are fixed-width flex items that don't wrap, which is what
        // clipped the "Add Assistant" button off the right edge.
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <span>{lang("Assistants")}</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {lang("Add Assistant")}
            </Button>
          </div>
        }
        bodyStyle={{ padding: 0 }}
      >
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={assistants}
          columns={columns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Modal
        title={editing ? lang("Edit Assistant") : lang("Add Assistant")}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        width={640}
        centered
        // A site-wide `.ant-modal-body { padding: 20px !important; }` rule
        // (main.css) beats a plain inline bodyStyle, so — matching how
        // .reschedule-modal etc. already opt out of it elsewhere in this
        // codebase — this scopes its own !important override instead. See
        // assets/styles/main.css.
        className="assistant-form-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label={lang("Full Name")} rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="email" label={lang("Email")} rules={[{ required: !editing, type: "email" }]}>
            <Input size="large" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="designation" label={lang("Designation")}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="assistantRoleLabel" label={lang("Role")}>
            <Input size="large" placeholder="Marketing Assistant, Content Manager, Custom…" />
          </Form.Item>
          <Form.Item name="permissions" label={lang("Permissions")} style={{ marginBottom: 0 }}>
            <Checkbox.Group style={{ width: "100%" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {sortCatalogForDisplay(catalog).map(([category, perms]) => {
                  const meta = CATEGORY_META[category];
                  const Icon = meta?.icon || AppstoreOutlined;
                  const label = meta?.label || category;
                  return (
                    <div
                      key={category}
                      style={{ border: "1px solid #D2E2EC", borderRadius: 10, background: "#EAF1F6", padding: "14px 16px" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13, letterSpacing: 0.3, textTransform: "uppercase", color: "#071D31", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #D2E2EC" }}>
                        <Icon style={{ color: "#3B6B94", fontSize: 15 }} />
                        {label}
                      </div>
                      {category === "content" ? (
                        // Post and Blog permissions get their own column
                        // each — see splitPostsAndBlogs above. Rendered as
                        // ONE grid (not two independent stacked flex
                        // columns) so post[i] and blog[i] always land in
                        // the same grid row and are forced to the same
                        // height — two separate columns each sizing to
                        // their own content has no way to guarantee that
                        // and drifted out of line row by row.
                        (() => {
                          const { posts, blogs, other } = splitPostsAndBlogs(perms);
                          const rowCount = Math.max(posts.length, blogs.length);
                          const rows = Array.from({ length: rowCount }, (_, i) => [posts[i], blogs[i]]);
                          return (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", rowGap: 10, columnGap: 24, alignItems: "center" }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#3B6B94", textTransform: "uppercase", letterSpacing: 0.3 }}>
                                {lang("Post")}
                              </span>
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#3B6B94", textTransform: "uppercase", letterSpacing: 0.3 }}>
                                {lang("Blog")}
                              </span>
                              {rows.map(([postPerm, blogPerm], i) => (
                                <React.Fragment key={i}>
                                  {postPerm ? <Checkbox value={postPerm.key} style={{ marginLeft: 0 }}>{postPerm.label}</Checkbox> : <span />}
                                  {blogPerm ? <Checkbox value={blogPerm.key} style={{ marginLeft: 0 }}>{blogPerm.label}</Checkbox> : <span />}
                                </React.Fragment>
                              ))}
                              {other.map((perm) => (
                                <Checkbox key={perm.key} value={perm.key} style={{ gridColumn: "1 / -1", marginLeft: 0 }}>
                                  {perm.label}
                                </Checkbox>
                              ))}
                            </div>
                          );
                        })()
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", rowGap: 10, columnGap: 12, alignItems: "center" }}>
                          {perms.map((perm) => (
                            <Checkbox key={perm.key} value={perm.key} style={{ marginLeft: 0 }}>
                              {perm.label}
                            </Checkbox>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {auditFor && (
        <Modal
          title={`${lang("Activity Log")} — ${auditFor.name}`}
          open
          onCancel={() => setAuditFor(null)}
          footer={null}
          width={640}
          centered
          className="assistant-form-modal"
        >
          <Spin spinning={auditLoading}>
            {auditLogs.length === 0 && !auditLoading ? (
              <Empty description={lang("No activity yet")} style={{ padding: "24px 0" }} />
            ) : (
              <List
                dataSource={auditLogs}
                renderItem={(row) => {
                  const isSuccess = row.result !== "denied";
                  return (
                    <List.Item style={{ padding: "14px 0", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", gap: 12, width: "100%" }}>
                        {isSuccess ? (
                          <CheckCircleFilled style={{ color: "#2CB673", fontSize: 18, marginTop: 2 }} />
                        ) : (
                          <CloseCircleFilled style={{ color: "#FF3030", fontSize: 18, marginTop: 2 }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: "var(--brand-900, #071D31)", lineHeight: 1.5 }}>
                            {row.message || `${row.actorName || row.assistantName} performed an action.`}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, color: "#8C8C8C" }}>{formatWhen(row.created_at)}</span>
                            {!isSuccess && (
                              <Tag color="red" style={{ marginInlineEnd: 0 }}>
                                {lang("Denied")}
                              </Tag>
                            )}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Spin>
        </Modal>
      )}

      <Modal
        title={resetPasswordFor ? `${lang("Reset Password")} — ${resetPasswordFor.name}` : lang("Reset Password")}
        open={!!resetPasswordFor}
        onCancel={() => setResetPasswordFor(null)}
        onOk={() => resetPasswordForm.submit()}
        centered
        destroyOnClose
      >
        <Form form={resetPasswordForm} layout="vertical" onFinish={submitResetPassword}>
          <p style={{ marginBottom: 16, color: "#3B6B94" }}>
            {lang("Set a new password for this assistant. They will be signed out of all existing sessions.")}
          </p>
          <Form.Item
            label={lang("New Password")}
            name="newPassword"
            rules={[
              { required: true, message: lang("Please input the new password!") },
              { min: 8, message: lang("Password must be at least 8 characters long!") },
              { max: 100, message: lang("Password cannot exceed 100 characters") },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
                message: lang("Password must contain at least one uppercase, one lowercase, one number, and one special character!"),
              },
            ]}
          >
            <Input.Password size="large" placeholder={lang("*************")} suffix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            label={lang("Re-enter New Password")}
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: lang("Please confirm the new password!") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(lang("Passwords do not match!")));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder={lang("*************")} suffix={<LockOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssistantsTab;
